import type {
  EvidenceQualification,
  EvidenceSection,
  EvidenceSourceGrade,
} from '../types/siteEvidence';

/** Higher numbers are more authoritative. Confidence never changes this order. */
export const EVIDENCE_SOURCE_GRADE_RANK: Readonly<Record<EvidenceSourceGrade, number>> = {
  'verified-professional': 5,
  'authoritative-government': 4,
  'reviewed-landgate': 3,
  'regional-screening': 2,
  inference: 1,
};

export interface ResolvedEvidenceSourcePolicy {
  grade: EvidenceSourceGrade;
  qualification: EvidenceQualification;
  /** True only when optional policy fields had to be inferred for a legacy SED. */
  compatibilityDefaulted: boolean;
}

const GOVERNMENT_SOURCE_MARKERS = [
  'fema',
  'usgs',
  'united states geological survey',
  'usda',
  'ssurgo',
  'nrc ',
  'nrc/',
  'national park service',
  'nps ',
  'census bureau',
  'u.s. census',
  'eia ',
  'energy information administration',
  'noaa',
  'epa ',
  'environmental protection agency',
] as const;

const INFERENCE_SOURCE_MARKERS = [
  'synthetic',
  'inference',
  'relief proxy',
  'estimated',
  'ai site-specific research',
  'scenario-derived',
  'modelled',
  'modeled',
] as const;

function inferLegacyGrade(section: EvidenceSection<unknown>): EvidenceSourceGrade {
  if (
    section.status === 'verified-from-doc'
    || section.provenance === 'doc'
    || section.provenance === 'doc-override'
    || section.provenance === 'provider+doc'
  ) return 'verified-professional';

  const source = section.source.toLowerCase();
  // Unreviewed LandGate is map-only and must never be upgraded by this reader.
  if (source.includes('landgate')) {
    return source.includes('reviewed') || source.includes('promoted')
      ? 'reviewed-landgate'
      : 'regional-screening';
  }
  if (GOVERNMENT_SOURCE_MARKERS.some(marker => source.includes(marker))) {
    return 'authoritative-government';
  }
  if (
    section.status === 'verified-from-research'
    || section.status === 'verified-from-report'
    || section.status === 'verified-from-context'
    || section.status === 'verified-from-intent'
    || INFERENCE_SOURCE_MARKERS.some(marker => source.includes(marker))
  ) return 'inference';
  return 'regional-screening';
}

function inferLegacyQualification(
  section: EvidenceSection<unknown>,
  grade: EvidenceSourceGrade,
): EvidenceQualification {
  if (section.status === 'unavailable' || section.data == null) return 'unavailable';
  if (grade === 'verified-professional') return 'verified';
  if (grade === 'inference') return 'estimated';
  return 'screening';
}

/**
 * Read optional policy fields without requiring a Firestore migration.
 * Ambiguous legacy values default downwards; this helper never promotes a
 * source based on confidence alone.
 */
export function resolveEvidenceSourcePolicy(
  section: EvidenceSection<unknown>,
): ResolvedEvidenceSourcePolicy {
  const grade = section.sourceGrade ?? inferLegacyGrade(section);
  const qualification = section.qualification
    ?? inferLegacyQualification(section, grade);
  return {
    grade,
    qualification,
    compatibilityDefaulted: section.sourceGrade == null || section.qualification == null,
  };
}

export function compareEvidenceSourceGrades(
  left: EvidenceSourceGrade,
  right: EvidenceSourceGrade,
): -1 | 0 | 1 {
  const delta = EVIDENCE_SOURCE_GRADE_RANK[left] - EVIDENCE_SOURCE_GRADE_RANK[right];
  return delta === 0 ? 0 : delta > 0 ? 1 : -1;
}

export type EvidenceReplacementDecision =
  | 'replace-lower-grade'
  | 'retain-higher-grade'
  | 'same-source-value'
  | 'preserve-conflict';

/**
 * Deterministic precedence decision. Equal-grade disagreements remain visible
 * conflicts; they are never resolved by call order or confidence.
 */
export function decideEvidenceReplacement(args: {
  existingGrade: EvidenceSourceGrade;
  incomingGrade: EvidenceSourceGrade;
  valuesEqual: boolean;
}): EvidenceReplacementDecision {
  const comparison = compareEvidenceSourceGrades(args.existingGrade, args.incomingGrade);
  if (comparison < 0) return 'replace-lower-grade';
  if (comparison > 0) return 'retain-higher-grade';
  return args.valuesEqual ? 'same-source-value' : 'preserve-conflict';
}
